import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { ScreenSize } from 'lib/constants';
import { randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  doAppFileGetFilesFromPicker,
  doEngageDownloadFlatReferences,
  doEngageDownloadTemplate,
  doEngageUploadContacts,
  eventFilesAdded,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { ICONS } from 'components/Icon';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FileTypeGroupsEnum } from 'service/file/types';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { CardGeneric } from 'components/CardGeneric';
import { Txt } from 'components/Txt';

export const EngageContactUploadsModal: React.FC<
  ModalScreenProps<ModalScreens.EngageContactUpload>
> = ({ route }) => {
  const eventKey = React.useRef(randomKey());

  const { key, propertyId } = route.params;

  const dispatch = useDispatch();

  // get propertyId value
  React.useEffect(() => {
    const events = [
      doAddEvent(eventFilesAdded, (event) => {
        if (event.payload.key === eventKey.current) {
          const file = event.payload.files[0];
          if (file) {
            // dispatch action to upload file
            dispatch(
              doEngageUploadContacts({
                key,
                propertyId: propertyId,
                fileName: file.name,
                file: file,
              }),
            );
          }
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [dispatch, propertyId, key]);

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);
  useEsc(handleClose);

  const { bottom: safeAreaBottom } = useSafeAreaInsets();
  const isWeb = useScreenSize() === ScreenSize.large;
  const scrollPaddingBottom = isWeb
    ? theme.paddingPageVertical * 10 + safeAreaBottom
    : theme.paddingPageVertical * 10 + safeAreaBottom;

  return (
    <PageScrollView
      isInModal={true}
      ignoreSafeAreaBottom
      contentPaddingBottom={scrollPaddingBottom}
      contentPaddingTop={20}
      header={
        <NavigationHeader
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          heading="Import Contacts"
        />
      }
    >
      <CardGeneric>
        <Txt>
          Upload a completed Contacts CSV using the import template available
          below.
        </Txt>
      </CardGeneric>
      <Card>
        <CardRow
          label="Upload Contacts CSV"
          icon={ICONS.UpSolid}
          iconColor={theme.colors.blue}
          labelColor={theme.colors.blue}
          onPress={() =>
            dispatch(
              doAppFileGetFilesFromPicker({
                eventKey: eventKey.current,
                returnType: 'event',
                multiSelect: false,
                allowedFileGroups: [FileTypeGroupsEnum.text],
              }),
            )
          }
        />
      </Card>

      <Card label="Downloads">
        <CardRow
          label="Download Import Template"
          icon={ICONS.DownSolid}
          iconColor={theme.colors.blue}
          labelColor={theme.colors.blue}
          onPress={() =>
            dispatch(
              doEngageDownloadTemplate({
                key: eventKey.current,
              }),
            )
          }
        />
        <CardRow
          label="Download Flat References"
          icon={ICONS.DownSolid}
          iconColor={theme.colors.blue}
          labelColor={theme.colors.blue}
          onPress={() =>
            dispatch(
              doEngageDownloadFlatReferences({
                key: eventKey.current,
                propertyId: propertyId,
              }),
            )
          }
        />
      </Card>
    </PageScrollView>
  );
};
